import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, UserCog, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const emptyCreate = { email: "", password: "", name: "", role: "user", can_view_all_rapportini: false, can_view_off_rapportini: false, can_add_employees: false, can_add_commesse: false, can_view_risorse: false, can_edit_risorse: false, can_view_dashboard: false, can_manage_settings: false, can_export_gestionale: true, can_manage_users: true, can_view_stampe_ore: true, avviso_accessi: false, ricevi_email_rapportini: true, show_budget_rapportino: true, show_daily_tips: false, show_last_export: true, backup_email_enabled: false, backup_email_freq: "monthly" };

export default function UtentiPage() {
  const { user: currentUser } = useAuth();
  // Iter 41: admin "sola lettura" sulla gestione utenti
  const readOnly = currentUser?.can_manage_users === false;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCreate);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setItems(data || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Caricamento utenti non riuscito";
      toast.error(typeof msg === "string" ? msg : "Caricamento utenti non riuscito");
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setForm(emptyCreate); setEditingId(null); setOpen(true); };
  const startEdit = (u) => {
    setForm({
      email: u.email,
      name: u.name,
      role: u.role,
      password: "",
      can_view_all_rapportini: !!u.can_view_all_rapportini,
      can_view_off_rapportini: !!u.can_view_off_rapportini,
      can_add_employees: !!u.can_add_employees,
      can_add_commesse: !!u.can_add_commesse,
      can_view_risorse: !!u.can_view_risorse,
      can_edit_risorse: !!u.can_edit_risorse,
      can_view_dashboard: !!u.can_view_dashboard,
      can_view_controllo_ore: u.can_view_controllo_ore ?? (u.role === "viewer" ? u.can_view_stampe_ore !== false : true),
      can_edit_controllo_ore: u.can_edit_controllo_ore ?? u.role !== "viewer",
      can_edit_budget: !!u.can_edit_budget,
      can_view_dashboard_commesse: u.can_view_dashboard_commesse !== false,
      show_budget_alert: u.show_budget_alert !== false,
      show_budget_rapportino: u.show_budget_rapportino !== false,
      can_manage_settings: u.can_manage_settings !== false,
      can_export_gestionale: u.can_export_gestionale !== false,
      can_manage_users: u.can_manage_users !== false,
      can_view_stampe_ore: u.can_view_stampe_ore !== false,
      avviso_accessi: !!u.avviso_accessi,
      ricevi_email_rapportini: u.ricevi_email_rapportini !== false,
      show_daily_tips: !!u.show_daily_tips,
      show_last_export: u.show_last_export !== false,
      backup_email_enabled: !!u.backup_email_enabled,
      backup_email_freq: u.backup_email_freq || "monthly",
      backup_email_last_at: u.backup_email_last_at || "",
    });
    setEditingId(u.id);
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editingId) {
        const payload = {
          name: form.name,
          role: form.role,
          can_view_all_rapportini: !!form.can_view_all_rapportini,
          can_view_off_rapportini: !!form.can_view_off_rapportini,
          can_add_employees: !!form.can_add_employees,
          can_add_commesse: !!form.can_add_commesse,
          can_view_risorse: !!form.can_view_risorse,
          can_edit_risorse: !!form.can_edit_risorse,
          can_view_dashboard: !!form.can_view_dashboard,
          can_view_controllo_ore: !!form.can_view_controllo_ore,
          can_edit_controllo_ore: !!form.can_edit_controllo_ore,
          can_edit_budget: !!form.can_edit_budget,
          can_view_dashboard_commesse: !!form.can_view_dashboard_commesse,
          show_budget_alert: !!form.show_budget_alert,
          show_budget_rapportino: !!form.show_budget_rapportino,
          can_manage_settings: !!form.can_manage_settings,
          can_export_gestionale: !!form.can_export_gestionale,
          can_manage_users: !!form.can_manage_users,
          can_view_stampe_ore: !!form.can_view_stampe_ore,
          avviso_accessi: !!form.avviso_accessi,
          ricevi_email_rapportini: !!form.ricevi_email_rapportini,
          show_daily_tips: !!form.show_daily_tips,
          show_last_export: !!form.show_last_export,
          backup_email_enabled: !!form.backup_email_enabled,
          backup_email_freq: form.backup_email_freq || "monthly",
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editingId}`, payload);
      } else {
        if (!form.email || !form.password) {
          toast.error("Email e password sono obbligatori");
          return;
        }
        await api.post("/users", form);
      }
      toast.success("Utente salvato");
      setOpen(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Salvataggio non riuscito";
      toast.error(typeof msg === "string" ? msg : "Salvataggio non riuscito");
    }
  };

  const [backupSending, setBackupSending] = useState(false);
  const sendUserBackup = async () => {
    if (!editingId) return;
    setBackupSending(true);
    try {
      const { data } = await api.post(`/backup/send-now/${editingId}`);
      if (data.status === "sent") {
        toast.success("Backup inviato! L'admin lo troverà nella sua email");
        if (data.last_at) setForm((f) => ({ ...f, backup_email_last_at: data.last_at }));
      } else if (data.status === "empty") {
        toast.info("Nessuna ora registrata da salvare");
      } else {
        toast.info(data.message || data.status);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio backup non riuscito");
    } finally {
      setBackupSending(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare l'utente?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Eliminato");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Eliminazione non riuscita";
      toast.error(typeof msg === "string" ? msg : "Eliminazione non riuscita");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            Amministrazione
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
            Utenti
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {readOnly
              ? "Sola lettura: puoi vedere gli utenti ma non crearli né modificarli."
              : "Gestisci chi può accedere all'applicazione."}
          </p>
        </div>
        {!readOnly && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={startCreate}
              data-testid="add-user-button"
              className="h-12 bg-[#0044FF] hover:bg-[#0033CC]"
            >
              <Plus className="h-5 w-5 mr-2" /> Nuovo utente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingId ? "Modifica utente" : "Nuovo utente"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="font-bold">Email *</Label>
                <Input
                  value={form.email}
                  type="email"
                  disabled={!!editingId}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-12 mt-1"
                  data-testid="user-email-input"
                />
              </div>
              <div>
                <Label className="font-bold">Nome</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="h-12 mt-1"
                  data-testid="user-name-input"
                />
              </div>
              <div>
                <Label className="font-bold">Ruolo</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) => setForm({ ...form, role: v })}
                >
                  <SelectTrigger className="h-12 mt-1" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Utente standard</SelectItem>
                    <SelectItem value="admin">Amministratore</SelectItem>
                    <SelectItem value="capo_cantiere" data-testid="role-capo-cantiere-option">
                      Capo cantiere / officina (inserisce ore, allega foto)
                    </SelectItem>
                    <SelectItem value="capo_officina" data-testid="role-capo-officina-option">
                      Capo officina (come capo cantiere + vede/modifica rapportini -OFF)
                    </SelectItem>
                    <SelectItem value="viewer" data-testid="role-viewer-option">
                      Visualizzatore (vede e stampa, non modifica)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === "admin" && (
                <div className="flex items-start gap-3 rounded-md border border-slate-300 bg-slate-50 p-3">
                  <input
                    id="adm-settings"
                    type="checkbox"
                    checked={!!form.can_manage_settings}
                    onChange={(e) =>
                      setForm({ ...form, can_manage_settings: e.target.checked })
                    }
                    data-testid="user-can-manage-settings-toggle"
                    className="mt-1 h-4 w-4"
                  />
                  <label htmlFor="adm-settings" className="text-sm text-slate-900 cursor-pointer">
                    <span className="font-bold">Accesso alle Impostazioni del software</span>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Se NON spuntato, l&apos;amministratore può fare tutto (rapportini,
                      vidimazioni, anagrafiche, utenti) ma la pagina{" "}
                      <span className="font-bold">Impostazioni</span> resta riservata
                      agli altri amministratori.
                    </div>
                  </label>
                </div>
              )}
              {form.role === "admin" && (
                <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                  <input
                    id="adm-export"
                    type="checkbox"
                    checked={!!form.can_export_gestionale}
                    onChange={(e) =>
                      setForm({ ...form, can_export_gestionale: e.target.checked })
                    }
                    data-testid="user-can-export-gestionale-toggle"
                    className="mt-1 h-4 w-4"
                  />
                  <label htmlFor="adm-export" className="text-sm text-emerald-900 cursor-pointer">
                    <span className="font-bold">Export al gestionale dal Riepilogo</span>
                    <div className="text-xs text-emerald-700 mt-0.5">
                      Se NON spuntato, nella pagina Riepilogo non vedrà il riquadro
                      &quot;Export per gestionale (.xlsx)&quot; e il download resta
                      riservato agli altri amministratori.
                    </div>
                  </label>
                </div>
              )}
              {form.role === "admin" && (
                <div className="flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-3">
                  <input
                    id="adm-users"
                    type="checkbox"
                    checked={!!form.can_manage_users}
                    onChange={(e) =>
                      setForm({ ...form, can_manage_users: e.target.checked })
                    }
                    data-testid="user-can-manage-users-toggle"
                    className="mt-1 h-4 w-4"
                  />
                  <label htmlFor="adm-users" className="text-sm text-blue-900 cursor-pointer">
                    <span className="font-bold">Gestione utenti (creazione/modifica)</span>
                    <div className="text-xs text-blue-700 mt-0.5">
                      Se NON spuntato, potrà solo VEDERE la lista utenti: creazione,
                      modifica ed eliminazione restano riservate agli altri
                      amministratori.
                    </div>
                  </label>
                </div>
              )}
              {form.role === "admin" && (
                <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                  <input
                    id="adm-email-rap"
                    type="checkbox"
                    checked={!!form.ricevi_email_rapportini}
                    onChange={(e) =>
                      setForm({ ...form, ricevi_email_rapportini: e.target.checked })
                    }
                    data-testid="user-ricevi-email-toggle"
                    className="mt-1 h-4 w-4"
                  />
                  <label htmlFor="adm-email-rap" className="text-sm text-emerald-900 cursor-pointer">
                    <span className="font-bold">Riceve i rapportini via email</span>
                    <div className="text-xs text-emerald-700 mt-0.5">
                      Se spuntato, riceve alla sua email ({form.email || "email account"}) sia i rapportini inviati via email
                      sia l'avviso con il link "Da vidimare" quando un capo cantiere salva un rapportino.
                      Se NON spuntato, non riceve nessuna email dei rapportini.
                    </div>
                  </label>
                </div>
              )}
              {form.role === "admin" && (
                <div className="flex items-start gap-3 rounded-md border border-sky-200 bg-sky-50 p-3">
                  <input
                    id="adm-accessi"
                    type="checkbox"
                    checked={!!form.avviso_accessi}
                    onChange={(e) =>
                      setForm({ ...form, avviso_accessi: e.target.checked })
                    }
                    data-testid="user-avviso-accessi-toggle"
                    className="mt-1 h-4 w-4"
                  />
                  <label htmlFor="adm-accessi" className="text-sm text-sky-900 cursor-pointer">
                    <span className="font-bold">Avviso accessi al software</span>
                    <div className="text-xs text-sky-700 mt-0.5">
                      Se spuntato, all&apos;ingresso in Home vedrà l&apos;elenco di
                      chi è entrato nel software dall&apos;ultima sua conferma,
                      con pulsante &quot;OK, visti&quot;.
                    </div>
                  </label>
                </div>
              )}
              {form.role === "admin" && (
                <div className="flex items-start gap-3 rounded-md border border-sky-200 bg-sky-50 p-3">
                  <input
                    id="adm-last-export"
                    type="checkbox"
                    checked={!!form.show_last_export}
                    onChange={(e) =>
                      setForm({ ...form, show_last_export: e.target.checked })
                    }
                    data-testid="user-show-last-export-toggle"
                    className="mt-1 h-4 w-4"
                  />
                  <label htmlFor="adm-last-export" className="text-sm text-sky-900 cursor-pointer">
                    <span className="font-bold">Ultima esportazione al gestionale in Home/Riepilogo</span>
                    <div className="text-xs text-sky-700 mt-0.5">
                      Se spuntato, in Home e nel Riepilogo vedrà fino a quale data
                      rapportino copre l&apos;ultima esportazione Excel al gestionale.
                    </div>
                  </label>
                </div>
              )}
              {form.role === "admin" && (
                <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-2" data-testid="user-backup-section">
                  <div className="flex items-start gap-3">
                    <input
                      id="adm-backup"
                      type="checkbox"
                      checked={!!form.backup_email_enabled}
                      onChange={(e) =>
                        setForm({ ...form, backup_email_enabled: e.target.checked })
                      }
                      data-testid="user-backup-toggle"
                      className="mt-1 h-4 w-4"
                    />
                    <label htmlFor="adm-backup" className="text-sm text-blue-900 cursor-pointer">
                      <span className="font-bold">🛡 Backup automatico ore via email</span>
                      <div className="text-xs text-blue-700 mt-0.5">
                        Se spuntato, riceverà sulla sua email un Excel con TUTTE le ore:
                        dettaglio completo, totali per commessa e ore mensili
                        dipendente per dipendente.
                      </div>
                    </label>
                  </div>
                  {!!form.backup_email_enabled && (
                    <div className="flex items-center gap-4 pl-7 text-sm text-blue-900">
                      <span>Frequenza:</span>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="backup-freq"
                          checked={form.backup_email_freq === "weekly"}
                          onChange={() => setForm({ ...form, backup_email_freq: "weekly" })}
                          data-testid="user-backup-freq-weekly"
                        />
                        Ogni settimana
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="backup-freq"
                          checked={form.backup_email_freq !== "weekly"}
                          onChange={() => setForm({ ...form, backup_email_freq: "monthly" })}
                          data-testid="user-backup-freq-monthly"
                        />
                        Ogni mese
                      </label>
                    </div>
                  )}
                  {editingId && (
                    <div className="flex flex-wrap items-center justify-between gap-2 pl-7">
                      <span className="text-xs text-blue-700" data-testid="user-backup-last-sent">
                        Ultimo backup inviato:{" "}
                        <span className="font-bold">
                          {form.backup_email_last_at
                            ? new Date(form.backup_email_last_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })
                            : "mai"}
                        </span>
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={sendUserBackup}
                        disabled={backupSending}
                        data-testid="user-backup-send-now-btn"
                      >
                        {backupSending ? "Invio…" : "Invia backup ora"}
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {form.role === "viewer" && (
                <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <input
                    id="viewer-stampe"
                    type="checkbox"
                    checked={!!form.can_view_stampe_ore}
                    onChange={(e) =>
                      setForm({ ...form, can_view_stampe_ore: e.target.checked })
                    }
                    data-testid="user-can-view-stampe-toggle"
                    className="mt-1 h-4 w-4"
                  />
                  <label htmlFor="viewer-stampe" className="text-sm text-amber-900 cursor-pointer">
                    <span className="font-bold">Accesso alle Stampe ore dipendenti</span>
                    <div className="text-xs text-amber-700 mt-0.5">
                      Se NON spuntato, il visualizzatore non vedrà la pagina{" "}
                      <span className="font-bold">Stampe ore dip.</span> e non potrà
                      consultare o stampare i report delle ore.
                    </div>
                  </label>
                </div>
              )}
              <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                <input
                  id="user-daily-tips"
                  type="checkbox"
                  checked={!!form.show_daily_tips}
                  onChange={(e) =>
                    setForm({ ...form, show_daily_tips: e.target.checked })
                  }
                  data-testid="user-show-daily-tips-toggle"
                  className="mt-1 h-4 w-4"
                />
                <label htmlFor="user-daily-tips" className="text-sm text-emerald-900 cursor-pointer">
                  <span className="font-bold">Frase del giorno in Home</span>
                  <div className="text-xs text-emerald-700 mt-0.5">
                    Se spuntato, entrando nella Home vedrà ogni giorno una frase
                    su collaborazione ed efficienza del team.
                  </div>
                </label>
              </div>
              {["capo_cantiere", "capo_officina"].includes(form.role) && (
                <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <input
                    id="cap-view-all"
                    type="checkbox"
                    checked={!!form.can_view_all_rapportini}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        can_view_all_rapportini: e.target.checked,
                      })
                    }
                    data-testid="user-can-view-all-toggle"
                    className="mt-1 h-4 w-4"
                  />
                  <label htmlFor="cap-view-all" className="text-sm text-amber-900 cursor-pointer">
                    <span className="font-bold">Può vedere i rapportini di tutti</span>
                    <div className="text-xs text-amber-700 mt-0.5">
                      Di default un capo cantiere vede solo i rapportini che ha creato lui.
                      Spunta per dargli visibilità globale (rimane comunque sola lettura
                      sugli altri).
                    </div>
                  </label>
                </div>
              )}
              {form.role === "capo_cantiere" && !form.can_view_all_rapportini && (
                <div className="flex items-start gap-3 rounded-md border border-sky-200 bg-sky-50 p-3">
                  <input
                    id="cap-view-off"
                    type="checkbox"
                    checked={!!form.can_view_off_rapportini}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        can_view_off_rapportini: e.target.checked,
                      })
                    }
                    data-testid="user-can-view-off-toggle"
                    className="mt-1 h-4 w-4"
                  />
                  <label htmlFor="cap-view-off" className="text-sm text-sky-900 cursor-pointer">
                    <span className="font-bold">Può vedere i rapportini delle commesse -OFF</span>
                    <div className="text-xs text-sky-700 mt-0.5">
                      Oltre ai propri, vede anche i rapportini creati da altri che
                      contengono almeno una commessa con codice terminante in
                      <span className="font-mono font-bold"> -OFF</span> (sola lettura).
                    </div>
                  </label>
                </div>
              )}
              {form.role === "capo_officina" && (
                <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800">
                  Il <span className="font-bold">capo officina</span> vede sempre tutti i
                  rapportini con commesse <span className="font-mono font-bold">-OFF</span>
                  {" "}(anche vidimati, anche di altri) e può modificarli finché non vidimati.
                </div>
              )}
              {["user", "capo_cantiere", "capo_officina"].includes(form.role) && (
                <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                  <input
                    id="cap-add-emp"
                    type="checkbox"
                    checked={!!form.can_add_employees}
                    onChange={(e) =>
                      setForm({ ...form, can_add_employees: e.target.checked })
                    }
                    data-testid="user-can-add-employees-toggle"
                    className="mt-1 h-4 w-4"
                  />
                  <label htmlFor="cap-add-emp" className="text-sm text-emerald-900 cursor-pointer">
                    <span className="font-bold">Può inserire nuovi dipendenti dai rapportini</span>
                    <div className="text-xs text-emerald-700 mt-0.5">
                      Mentre compila un rapportino, se digita un nome non presente in
                      anagrafica potrà creare il dipendente al volo. Il dipendente resta
                      <span className="font-bold"> "da confermare"</span> e ti verrà
                      segnalato con un avviso al tuo prossimo accesso.
                    </div>
                  </label>
                </div>
              )}
              {["user", "capo_cantiere", "capo_officina"].includes(form.role) && (
                <div className="flex items-start gap-3 rounded-md border border-indigo-200 bg-indigo-50 p-3">
                  <input
                    id="cap-add-comm"
                    type="checkbox"
                    checked={!!form.can_add_commesse}
                    onChange={(e) =>
                      setForm({ ...form, can_add_commesse: e.target.checked })
                    }
                    data-testid="user-can-add-commesse-toggle"
                    className="mt-1 h-4 w-4"
                  />
                  <label htmlFor="cap-add-comm" className="text-sm text-indigo-900 cursor-pointer">
                    <span className="font-bold">Può creare nuove commesse dai rapportini</span>
                    <div className="text-xs text-indigo-700 mt-0.5">
                      Se digita un codice commessa non presente in elenco potrà crearla
                      al volo (il cliente viene suggerito dal codice EG-XX se già in
                      anagrafica). La commessa resta <span className="font-bold">"da confermare"</span>
                      {" "}e ti verrà segnalata con un avviso al tuo prossimo accesso.
                    </div>
                  </label>
                </div>
              )}
              {form.role !== "admin" && (
                <div className="space-y-2 rounded-md border border-yellow-300 bg-yellow-50 p-3" data-testid="user-risorse-perms">
                  <div className="text-xs font-bold uppercase tracking-wide text-yellow-900">Parco risorse e Programma giornaliero</div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.can_view_risorse || !!form.can_edit_risorse}
                      disabled={!!form.can_edit_risorse}
                      onChange={(e) => setForm({ ...form, can_view_risorse: e.target.checked })}
                      data-testid="user-can-view-risorse-toggle"
                      className="mt-1 h-4 w-4"
                    />
                    <span className="text-sm text-yellow-900">
                      <span className="font-bold">Può vedere Risorse e Programma</span>
                      <div className="text-xs text-yellow-800 mt-0.5">Personale, mezzi, attrezzature, sedi, scadenze, lavagna giornaliera e stampa A3 in sola lettura.</div>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.can_edit_risorse}
                      onChange={(e) => setForm({ ...form, can_edit_risorse: e.target.checked, can_view_risorse: e.target.checked || form.can_view_risorse })}
                      data-testid="user-can-edit-risorse-toggle"
                      className="mt-1 h-4 w-4"
                    />
                    <span className="text-sm text-yellow-900">
                      <span className="font-bold">Può anche modificare</span>
                      <div className="text-xs text-yellow-800 mt-0.5">Inserire/modificare risorse, spostare le tessere nel programma, gestire assenze e note.</div>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.can_view_dashboard}
                      onChange={(e) => setForm({ ...form, can_view_dashboard: e.target.checked })}
                      data-testid="user-can-view-dashboard-toggle"
                      className="mt-1 h-4 w-4"
                    />
                    <span className="text-sm text-yellow-900">
                      <span className="font-bold">Può vedere la Dashboard aziendale</span>
                      <div className="text-xs text-yellow-800 mt-0.5">Quadro in tempo reale: chi è dove, mezzi, assenze, rapportini del giorno e scadenze (sola lettura).</div>
                    </span>
                  </label>
                </div>
              )}
              {form.role !== "admin" && (
                <div className="space-y-2 rounded-md border border-indigo-300 bg-indigo-50 p-3" data-testid="user-commesse-perms">
                  <div className="text-xs font-bold uppercase tracking-wide text-indigo-900">Commesse e budget</div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={!!form.can_view_dashboard_commesse} onChange={(e) => setForm({ ...form, can_view_dashboard_commesse: e.target.checked })} data-testid="user-can-view-dash-commesse-toggle" className="mt-1 h-4 w-4" />
                    <span className="text-sm text-indigo-900"><span className="font-bold">Può vedere la Dashboard commesse</span><div className="text-xs text-indigo-800 mt-0.5">Avanzamento ore vs budget delle commesse recenti (default: sì).</div></span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={!!form.show_budget_alert} onChange={(e) => setForm({ ...form, show_budget_alert: e.target.checked })} data-testid="user-show-budget-alert-toggle" className="mt-1 h-4 w-4" />
                    <span className="text-sm text-indigo-900"><span className="font-bold">Mostra in Home "Commesse vicine al budget ore"</span><div className="text-xs text-indigo-800 mt-0.5">Avviso giallo con le commesse oltre l'80% del budget (default: sì).</div></span>
                  </label>
                </div>
              )}
              {form.role !== "viewer" && (
                <label className="flex items-start gap-3 cursor-pointer rounded-md border border-teal-300 bg-teal-50 p-3" data-testid="user-show-budget-rapportino-perm">
                  <input type="checkbox" checked={!!form.show_budget_rapportino} onChange={(e) => setForm({ ...form, show_budget_rapportino: e.target.checked })} data-testid="user-show-budget-rapportino-toggle" className="mt-1 h-4 w-4" />
                  <span className="text-sm text-teal-900">
                    <span className="font-bold">Mostra "Budget ore commesse" nel rapportino</span>
                    <div className="text-xs text-teal-800 mt-0.5">La riga verde/gialla/rossa con budget, ore usate e residuo che compare mentre compila il rapportino (default: sì).</div>
                  </span>
                </label>
              )}
              {form.role !== "admin" && form.role !== "viewer" && (
                <label className="flex items-start gap-3 cursor-pointer rounded-md border border-violet-300 bg-violet-50 p-3" data-testid="user-budget-perm">
                  <input type="checkbox" checked={!!form.can_edit_budget} onChange={(e) => setForm({ ...form, can_edit_budget: e.target.checked })} data-testid="user-can-edit-budget-toggle" className="mt-1 h-4 w-4" />
                  <span className="text-sm text-violet-900">
                    <span className="font-bold">Può impostare il budget ore delle commesse</span>
                    <div className="text-xs text-violet-800 mt-0.5">Di default solo gli admin. Chi compila i rapportini vede le ore residue del budget se la riga qui sopra è attiva.</div>
                  </span>
                </label>
              )}
              {form.role !== "admin" && (
                <div className="space-y-2 rounded-md border border-red-300 bg-red-50 p-3" data-testid="user-controllo-perms">
                  <div className="text-xs font-bold uppercase tracking-wide text-red-900">Controllo giornate mancanti</div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.can_view_controllo_ore || !!form.can_edit_controllo_ore}
                      disabled={!!form.can_edit_controllo_ore}
                      onChange={(e) => setForm({ ...form, can_view_controllo_ore: e.target.checked })}
                      data-testid="user-can-view-controllo-toggle"
                      className="mt-1 h-4 w-4"
                    />
                    <span className="text-sm text-red-900">
                      <span className="font-bold">Può vedere il controllo</span>
                      <div className="text-xs text-red-800 mt-0.5">Elenco dei dipendenti senza ore nei giorni lavorativi (default: chi ha le Stampe ore).</div>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.can_edit_controllo_ore}
                      disabled={form.role === "viewer"}
                      onChange={(e) => setForm({ ...form, can_edit_controllo_ore: e.target.checked, can_view_controllo_ore: e.target.checked || form.can_view_controllo_ore })}
                      data-testid="user-can-edit-controllo-toggle"
                      className="mt-1 h-4 w-4"
                    />
                    <span className="text-sm text-red-900">
                      <span className="font-bold">Può registrare assenze / ignorare giornate</span>
                      <div className="text-xs text-red-800 mt-0.5">Ferie, malattia, infortunio, permesso: aggiornano anche il Programma giornaliero (default: chi compila rapportini).</div>
                    </span>
                  </label>
                </div>
              )}
              <div>
                <Label className="font-bold">
                  {editingId ? "Nuova password (opzionale)" : "Password *"}
                </Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  className="h-12 mt-1"
                  data-testid="user-password-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Annulla
              </Button>
              <Button
                onClick={save}
                data-testid="save-user-button"
                className="bg-[#0044FF] hover:bg-[#0033CC]"
              >
                Salva
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {items.length === 0 ? (
        <Card className="p-10 text-center">
          <UserCog className="h-10 w-10 mx-auto text-slate-300" />
          <div className="font-heading font-bold text-lg mt-3">Nessun utente</div>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((u) => (
            <Card
              key={u.id}
              className="p-4 flex items-center gap-3"
              data-testid={`user-row-${u.id}`}
            >
              <div className="h-12 w-12 rounded-md bg-slate-100 grid place-items-center font-heading font-black text-base uppercase">
                {(u.name || u.email)[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold truncate">
                  {u.name || u.email}
                </div>
                <div className="text-xs text-slate-500 truncate">{u.email}</div>
                {(u.can_view_all_rapportini || u.can_view_off_rapportini || u.role === "capo_officina" || u.can_add_employees || u.can_add_commesse || u.can_view_risorse || u.can_edit_risorse || u.can_view_dashboard || u.can_view_controllo_ore === false || u.can_edit_controllo_ore === false || (u.role === "viewer" && u.can_view_stampe_ore === false) || (u.role === "admin" && (u.can_manage_settings === false || u.can_export_gestionale === false || u.can_manage_users === false || u.avviso_accessi || u.ricevi_email_rapportini === false))) && (
                  <div className="flex flex-wrap gap-1 mt-1" data-testid={`user-perms-${u.id}`}>
                    {u.role === "admin" && u.ricevi_email_rapportini === false && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-700" title="NON riceve i rapportini via email">
                        ✉ no email rapportini
                      </span>
                    )}                    {u.role === "admin" && u.avviso_accessi && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-100 text-sky-800" title="Riceve in Home l'avviso di chi è entrato nel software">
                        👁 avvisi accessi
                      </span>
                    )}                    {u.role === "viewer" && u.can_view_stampe_ore === false && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-700" title="Visualizzatore senza Stampe ore dipendenti">
                        🔒 no Stampe ore
                      </span>
                    )}                    {u.role === "admin" && u.can_manage_settings === false && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-700" title="Amministratore senza accesso alle Impostazioni">
                        🔒 no Impostazioni
                      </span>
                    )}
                    {u.role === "admin" && u.can_export_gestionale === false && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-700" title="Amministratore senza export al gestionale">
                        🔒 no Export gestionale
                      </span>
                    )}
                    {u.role === "admin" && u.can_manage_users === false && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-700" title="Amministratore con utenti in sola lettura">
                        🔒 utenti in sola lettura
                      </span>
                    )}                    {u.can_view_all_rapportini && !["admin", "viewer"].includes(u.role) && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" title="Vede tutti i rapportini">
                        👁 tutti
                      </span>
                    )}
                    {(u.role === "capo_officina" || u.can_view_off_rapportini) && u.role !== "admin" && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-100 text-sky-800" title="Vede i rapportini delle commesse -OFF">
                        -OFF
                      </span>
                    )}
                    {u.can_add_employees && u.role !== "admin" && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800" title="Può inserire nuovi dipendenti (da confermare)">
                        +dipendenti
                      </span>
                    )}
                    {u.can_add_commesse && u.role !== "admin" && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800" title="Può creare nuove commesse (da confermare)">
                        +commesse
                      </span>
                    )}
                    {(u.can_view_controllo_ore === false || u.can_edit_controllo_ore === false) && u.role !== "admin" && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-800" title="Controllo giornate mancanti limitato">
                        {u.can_view_controllo_ore === false ? "no controllo" : "controllo 👁"}
                      </span>
                    )}
                    {u.can_view_dashboard && u.role !== "admin" && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-800 text-white" title="Può vedere la Dashboard aziendale">dashboard</span>
                    )}
                    {(u.can_view_risorse || u.can_edit_risorse) && u.role !== "admin" && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800" title={u.can_edit_risorse ? "Può vedere e modificare Risorse/Programma" : "Può vedere Risorse/Programma"}>
                        {u.can_edit_risorse ? "risorse ✎" : "risorse 👁"}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <span
                className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md ${
                  u.role === "admin"
                    ? "bg-[#0044FF] text-white"
                    : u.role === "viewer"
                    ? "bg-amber-100 text-amber-800"
                    : u.role === "capo_cantiere"
                    ? "bg-emerald-100 text-emerald-800"
                    : u.role === "capo_officina"
                    ? "bg-sky-100 text-sky-800"
                    : "bg-slate-200 text-slate-700"
                }`}
              >
                {u.role === "viewer"
                  ? "visualizzatore"
                  : u.role === "capo_cantiere"
                  ? "capo cantiere"
                  : u.role === "capo_officina"
                  ? "capo officina"
                  : u.role}
              </span>
              <div className="flex gap-2">
                {!readOnly && (
                <Button
                  size="icon"
                  variant="outline"
                  className="h-10 w-10"
                  onClick={() => startEdit(u)}
                  data-testid={`edit-user-${u.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                )}
                {!readOnly && u.id !== currentUser?.id && (
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-10 w-10 text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => remove(u.id)}
                    data-testid={`delete-user-${u.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
